import { Injectable } from '@angular/core';

// AFs 
import { AngularFirestore } from '@angular/fire/firestore';
import { Http, RequestOptions, Headers } from '@angular/http';

// Services
import { DataService } from './data.service';
import { DebugService } from './debug.service';

// RXJS
import { map, timeout } from 'rxjs/operators';

// Model
import { UserInfo, SchoolInfo } from '../model/free-trial';

@Injectable({
  	providedIn: 'root'
})
export class FreeTrialService {

  	constructor(
		private afs: AngularFirestore,
		private dataSvc: DataService,
		private http: Http,
		private debugSvc: DebugService,
	) { }

	 // Check to see if a school for a given name already exists in the database.
	 checkForSchoolByName(schoolName: string, isPotentialSchool?: boolean): Promise<any> {
        let headers = new Headers({
			'Content-Type': 'application/json',
			'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
		});
		
		let options = new RequestOptions({ headers });
		let body = JSON.stringify({ name: schoolName, isPotentialSchool });

		return new Promise((resolve,reject) => {
			this.http.post(this.dataSvc.getAPIBaseUrl() + "/isExistingSchoolName",body, options)
			.pipe(map(res => res.json()), timeout(30000))
			.toPromise()
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				resolve(null);
			});
		})
    }

    // Check to see if a school for a given name already exists in the database.
    checkForSchoolByAlias(schoolAliases: string[]): Promise<any> {
        let headers = new Headers({
			'Content-Type': 'application/json',
			'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
		});
		
		let options = new RequestOptions({ headers });
		let body = JSON.stringify({ aliases: schoolAliases });

		return new Promise((resolve,reject) => {
			this.http.post(this.dataSvc.getAPIBaseUrl() + "/isExistingSchoolAlias",body, options)
			.pipe(map(res => res.json()), timeout(30000))
			.toPromise()
			.then((res) => {
				resolve(res.results);
			})
			.catch((err) => {
				resolve(null);
			});
		})
    }

	// Create Free Trial School, add user as employee and users/user and daycare/users/user
	saveFreeTrialSchool(schoolInfo: SchoolInfo, userInfo: UserInfo): Promise<any> {
		let headers = new Headers({
			'Content-Type': 'application/json',
			'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
		});

		let newUserInfo = Object.assign({}, userInfo) as any;
		newUserInfo.name = newUserInfo.fName + " " + newUserInfo.lName;
		
		let newSchoolInfo = Object.assign({}, schoolInfo) as any;
		newSchoolInfo.adminName = newUserInfo.name;
		newSchoolInfo.adminEmail = newUserInfo.email;

        let options = new RequestOptions({ headers: headers });
        let body = JSON.stringify({
			daycare: newSchoolInfo, 
			userInfo: newUserInfo,
			timezoneOffset: new Date().getTimezoneOffset(),
			version: 2
		})

		return new Promise((resolve,reject) => {
			this.http.post(this.dataSvc.getAPIBaseUrl() + "/createFreeTrialDaycare",body, options)
			.pipe(map(res => res.json()), timeout(60000))
			.toPromise()
			.then((res) => {
				//console.log(res);
				resolve(res)
			})
			.catch((err) => {
				console.log(err);
				let errorObj = this.debugSvc.getErrorObject(err, 'save-free-trial-school', Object.assign({}, userInfo), 'saving free trial school');
				this.debugSvc.logErrorToDBV2(errorObj);
				reject(new Error('Failed to save your free trial account.'))
			});
		})
	}

	// Send Free Trial Confirmation Email to director
	sendFreeTrialConfirmation(daycareName: string) {
		let headers = new Headers({
			'Content-Type': 'application/json',
			'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
		});
		
		let options = new RequestOptions({ headers });
		let body = JSON.stringify({ daycareName });

		return new Promise((resolve,reject) => {
			this.http.post(this.dataSvc.getAPIBaseUrl() + "/sendFreeTrialSignupEmail",body, options)
			.pipe(map(res => res.json()), timeout(30000))
			.toPromise()
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				resolve(null);
			});
		})
	}

	async upgradeFreeTrial(daycareName: string, daycareInviteKey: string, cpPlan: string, daycareAccountID: string): Promise<any> {
		try { 
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUser(daycareName, daycareInviteKey)
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ daycareName, timezoneOffset: new Date().getTimezoneOffset(), cpPlan, accountID: daycareAccountID });

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/upgradeFreeTrialDaycare",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		}
		catch(e) {
			return Promise.reject(e);
		}
	}

	async sendFreeTrialUpgradeConfirmation(daycareName: string, daycareInviteKey: string, cpPlan: string): Promise<any> {
		try {
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUser(daycareName, daycareInviteKey)
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ daycareName, timezoneOffset: new Date().getTimezoneOffset(), cpPlan });

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/sendFreeTrialUpgradeConfirmation",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		}
		catch(e) {
			return Promise.reject(e);
		}
	}

	// Cancel free trial for this daycare
	async cancelFreeTrial(daycareName: string, daycareInviteKey: string, reason: string, expectations: string, daycareAccountID: string): Promise<any> {
		try { 
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUser(daycareName, daycareInviteKey)
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ daycareName, reason, expectationsMetResponse: expectations, timezoneOffset: new Date().getTimezoneOffset(), accountID: daycareAccountID });

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/cancelFreeTrialDaycare",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		}
		catch(e) {
			return Promise.reject(e);
		}
	}

	// Send cancel email to school director and designated CP members
	async sendCancelConfirmation(daycareName: string, daycareInviteKey: string): Promise<any> {
		try { 
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUser(daycareName, daycareInviteKey)
			});
			let options = new RequestOptions({ headers: headers });

			let body = {
				daycareName, 
				timezoneOffset: new Date().getTimezoneOffset()
			}

			let res = await this.http.post(this.dataSvc.getAPIBaseUrl() + '/sendFreeTrialCancelConfirmation', JSON.stringify(body), options).toPromise();

			return res;
		}
		catch(e) {
			return Promise.reject(e);
		}
	}

	// Creates Signup Lead in Firestore
	async createLeadFromUser(user: UserInfo): Promise<string> {
		try {			
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ 
				user: Object.assign({}, user),
				createdDate: new Date() 
			});

			let response: { message: string, leadID: string } = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/createLeadFromUser",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response.leadID;
		}
		catch(e) {
			return Promise.reject(e);
		}
	}

	// Updates Signup Lead in Firestore with Daycare info
	async updateLeadWithDaycare(leadID: string, daycare: SchoolInfo): Promise<any> {
		try {	
			let newDaycare = Object.assign({}, daycare);
			delete newDaycare['accountNumber'];

			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ leadID, daycare: newDaycare });

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/updateLeadWithDaycare",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		} catch (e) {
			return Promise.reject(e);
		}
	}

	// Queries CRM leads by email and returns lead id
	async queryLeadFromUser(user: UserInfo): Promise<string> {
		try {			
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ 
				fName: user.fName,
				email: user.email
			});

			let response: { leadID: string } = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/getLeadIDForFinishSignup",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response.leadID;
		}
		catch(e) {
			console.log("queryLeadFromUser error");
			console.log(e.message)
			return Promise.reject(e);
		}
	}

	// This function is called when a school completes their free trial registration. 
	async markLeadFulfilled(leadID: string, daycare: SchoolInfo): Promise<any> {
		try {	
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ leadID, daycare });

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/markLeadFulfilled",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		} catch (e) {
			return Promise.reject(e);
		}
	}

	// Call to create follow up emails to our lite/trial clients
	async createFollowUpEmails(daycareName: string, startDate: Date, email: string, leadID: string): Promise<any> {
		try {	
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ 
				daycareName, 
				startDate, 
				timezoneOffset: new Date().getTimezoneOffset(), 
				email,
				version: 2,
				leadID
			});

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/createFollowUpEmails",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		} catch (e) {
			return Promise.reject(e);
		}
	}

	// Call to delete follow up emails queued to send to our lite/trial clients
	async deleteFollowUpEmails(daycareName: string): Promise<any> {
		try {	
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ daycareName, timezoneOffset: new Date().getTimezoneOffset() });

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/deleteFollowUpEmails",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		} catch (e) {
			return Promise.reject(e);
		}
	}

	// Call to create the finish-signup email. This email is sent if a school director DOESNT complete the signup process completely
	async createFinishSignupEmail(fName: string, uid: string, startDate: Date, email: string, leadID: string): Promise<any> {
		try {	
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ fName, uid, startDate, timezoneOffset: new Date().getTimezoneOffset(), email, leadID });

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/scheduleFinishSignupEmail",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		} catch (e) {
			return Promise.reject(e);
		}
	}

	// Call to cancel the scheduled email that would've been sent to the school director encouraging them to complete the signup process
	async cancelFinishSignupEmail(uid: string): Promise<any> {
		try {	
			let headers = new Headers({
				'Content-Type': 'application/json',
				'Authorization' : this.dataSvc.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
			});
			
			let options = new RequestOptions({ headers });
			let body = JSON.stringify({ uid, timezoneOffset: new Date().getTimezoneOffset() });

			let response = await this.http.post(this.dataSvc.getAPIBaseUrl() + "/cancelFinishSignupEmail",body, options)
				.pipe(map(res => res.json()), timeout(30000))
				.toPromise();

			return response;
		} catch (e) {
			return Promise.reject(e);
		}
	}
}
